import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  TextField,
  MenuItem,
  Divider,
  Stack,
  alpha
} from '@mui/material';
import {
  EventAvailable as EventAvailableIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GridView as GridViewIcon,
  FormatListBulleted as TableListIcon,
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarIcon,
  MeetingRoom as RoomIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AttachMoney as MoneyIcon,
  Notes as NotesIcon,
  CheckCircle as ConfirmedIcon,
  HourglassEmpty as PendingIcon,
  Cancel as CancelledIcon,
  DoneAll as CompletedIcon
} from '@mui/icons-material';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { academyStore } from '../services/academyStore';
import { RoomRental, RoomRentalStatus, Room } from '../types/academy';
import { BlobticDataManager } from '../../Commun/services/blobticDataManager';

const emptyRental = (): Partial<RoomRental> => ({
  arm_idf: 0,
  code: '',
  lib: '',
  client_name: '',
  client_phone: '',
  client_email: '',
  start_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10) + 'T09:00',
  end_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10) + 'T12:00',
  price: 0,
  deposit: 0,
  status: 'CONFIRMED',
  notes: ''
});

const getStatusColor = (status: RoomRentalStatus) => {
  switch (status) {
    case 'CONFIRMED': return 'success';
    case 'PENDING': return 'warning';
    case 'CANCELLED': return 'error';
    case 'COMPLETED': return 'info';
    default: return 'default';
  }
};

const getStatusIcon = (status: RoomRentalStatus) => {
  switch (status) {
    case 'CONFIRMED': return <ConfirmedIcon fontSize="small" />;
    case 'PENDING': return <PendingIcon fontSize="small" />;
    case 'CANCELLED': return <CancelledIcon fontSize="small" />;
    case 'COMPLETED': return <CompletedIcon fontSize="small" />;
    default: return undefined;
  }
};

export const RoomRentalsPage: React.FC = () => {
  const { t } = useLanguage();

  // View state: 'list' (default) or 'form' (new/edit)
  const [view, setView] = useState<'list' | 'form'>('list');

  // List view mode: 'grid' or 'table' (initialized from user default preference)
  const [viewMode, setViewMode] = useDefaultViewMode();

  // Rentals & Rooms from academyStore
  const [rentalsList, setRentalsList] = useState<RoomRental[]>(() => academyStore.getRoomRentals());
  const [rooms, setRooms] = useState<Room[]>(() => academyStore.getRooms());

  // Form State
  const [formData, setFormData] = useState<Partial<RoomRental>>(emptyRental());

  // Reactive store subscription
  useEffect(() => {
    academyStore.ensureRoomRentalsLoaded();
    academyStore.ensureRoomsLoaded();
    setRentalsList(academyStore.getRoomRentals());
    setRooms(academyStore.getRooms());

    const unsubscribe = academyStore.subscribe(() => {
      setRentalsList(academyStore.getRoomRentals());
      setRooms(academyStore.getRooms());
    });

    const unsubscribeIdb = BlobticDataManager.subscribe((obj) => {
      if (obj === 'academy_room_rental') {
        academyStore.notify(['academy_room_rental']);
      }
    });

    return () => {
      unsubscribe();
      unsubscribeIdb();
    };
  }, []);

  // ── Navigation & Actions ───────────────────────────────────────────────────

  const openNew = () => {
    const initial = emptyRental();
    setFormData(initial);
    setView('form');
  };

  const openEdit = (rental: RoomRental) => {
    setFormData({ ...rental });
    setView('form');
  };

  const backToList = () => {
    setView('list');
    setFormData(emptyRental());
  };

  const setField = <K extends keyof RoomRental>(key: K, value: RoomRental[K]) =>
    setFormData(prev => ({ ...prev, [key]: value }));

  // ── Validation ─────────────────────────────────────────────────────────────

  const handleValidate = (): string | null => {
    if (!formData.arm_idf || formData.arm_idf <= 0) {
      return t('academy.room_rentals.validation.room_required') || 'Veuillez sélectionner une salle.';
    }
    if (!formData.client_name?.trim()) {
      return t('academy.room_rentals.validation.client_required') || 'Le nom du client / organisme est obligatoire.';
    }
    if (!formData.start_date) {
      return t('academy.room_rentals.validation.start_date_required') || 'La date de début est obligatoire.';
    }
    if (!formData.end_date) {
      return t('academy.room_rentals.validation.end_date_required') || 'La date de fin est obligatoire.';
    }
    if (new Date(formData.end_date) <= new Date(formData.start_date)) {
      return t('academy.room_rentals.validation.date_order') || 'La date de fin doit être postérieure à la date de début.';
    }
    return null;
  };

  // ── Delete Handler ─────────────────────────────────────────────────────────

  const handleDelete = async (rental: RoomRental) => {
    const confirmed = await blobticMessage.confirm({
      title: t('common.confirm.title.delete') || 'Confirmation',
      message: `Êtes-vous sûr de vouloir supprimer la location "${rental.client_name || rental.code}" ?`,
      severity: 'warning',
      confirmText: t('common.actions.confirm') || 'Confirmer',
      cancelText: t('common.actions.cancel') || 'Annuler'
    });
    if (!confirmed) return;

    try {
      const res = await academyStore.deleteRoomRental(rental.id);
      if (res.success) {
        blobticMessage.success(
          t('common.messages.success') || 'Succès',
          t('academy.room_rentals.deleted_success') || 'Location supprimée avec succès.'
        );
      } else {
        blobticMessage.error(
          t('common.messages.error') || 'Erreur',
          res.message || 'Échec de la suppression.'
        );
      }
    } catch (err: any) {
      blobticMessage.error(t('common.messages.error') || 'Erreur', err.message);
    }
  };

  // Map room numbers helper
  const getRoomLabel = (armIdf?: number) => {
    if (!armIdf) return '—';
    const found = rooms.find(r => r.id === armIdf);
    return found ? `${found.room_number} (${found.capacity} pl.)` : `#${armIdf}`;
  };

  // Format date helper
  const formatDateDisplay = (dateIso?: string) => {
    if (!dateIso) return '—';
    try {
      const d = new Date(dateIso);
      return d.toLocaleString('fr-DZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateIso;
    }
  };

  // ── TABLE COLUMNS ──────────────────────────────────────────────────────────

  const tableColumns = [
    {
      id: 'status',
      label: t('common.status') || 'Statut',
      width: '130px',
      render: (row: RoomRental) => (
        <Chip
          size="small"
          label={row.status}
          color={getStatusColor(row.status) as any}
          icon={getStatusIcon(row.status)}
          variant="filled"
          sx={{ fontWeight: 600 }}
        />
      )
    },
    {
      id: 'room_number',
      label: t('academy.room_rentals.room') || 'Salle',
      width: '140px',
      render: (row: RoomRental) => (
        <Chip
          icon={<RoomIcon fontSize="small" />}
          label={getRoomLabel(row.arm_idf)}
          size="small"
          variant="outlined"
          color="primary"
          sx={{ fontWeight: 600 }}
        />
      )
    },
    {
      id: 'client_name',
      label: t('academy.room_rentals.client') || 'Client / Organisme',
      render: (row: RoomRental) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.client_name}
          </Typography>
          {row.lib && row.lib !== `Location - ${row.client_name}` && (
            <Typography variant="caption" color="text.secondary">
              {row.lib}
            </Typography>
          )}
        </Box>
      )
    },
    {
      id: 'dates',
      label: t('academy.room_rentals.dates') || 'Période de Location',
      render: (row: RoomRental) => (
        <Typography variant="body2">
          {formatDateDisplay(row.start_date)} <br />
          <Typography component="span" variant="caption" color="text.secondary">
            → {formatDateDisplay(row.end_date)}
          </Typography>
        </Typography>
      )
    },
    {
      id: 'contact',
      label: t('common.contact') || 'Contact',
      render: (row: RoomRental) => (
        <Box sx={{ fontSize: '0.8rem' }}>
          {row.client_phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PhoneIcon fontSize="inherit" color="action" />
              <span>{row.client_phone}</span>
            </Box>
          )}
          {row.client_email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EmailIcon fontSize="inherit" color="action" />
              <span>{row.client_email}</span>
            </Box>
          )}
          {!row.client_phone && !row.client_email && '—'}
        </Box>
      )
    },
    {
      id: 'price',
      label: t('academy.room_rentals.price') || 'Tarif',
      width: '130px',
      render: (row: RoomRental) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
            {Number(row.price || 0).toLocaleString()} DZD
          </Typography>
          {Number(row.deposit || 0) > 0 && (
            <Typography variant="caption" color="text.secondary">
              Acompte: {Number(row.deposit).toLocaleString()} DZD
            </Typography>
          )}
        </Box>
      )
    }
  ];

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: LIST VIEW
  // ═══════════════════════════════════════════════════════════════════════════

  if (view === 'list') {
    return (
      <GenericPage
        title={t('academy.room_rentals.title') || 'Locations de salle'}
        icon={<EventAvailableIcon />}
        footerEnabled={false}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onNew={openNew}
        newButtonLabel={t('academy.room_rentals.new_rental') || 'Nouvelle Location'}
      >
        {viewMode === 'grid' ? (
          <Grid container spacing={2.5}>
            {rentalsList.length === 0 ? (
              <Grid item xs={12}>
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
                    borderRadius: 3,
                    border: '1px dashed',
                    borderColor: 'divider'
                  }}
                >
                  <EventAvailableIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 1.5, opacity: 0.6 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('academy.room_rentals.empty_title') || 'Aucune location enregistrée'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('academy.room_rentals.empty_subtitle') || 'Créez une nouvelle location pour réserver une salle.'}
                  </Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
                    {t('academy.room_rentals.new_rental') || 'Nouvelle Location'}
                  </Button>
                </Box>
              </Grid>
            ) : (
              rentalsList.map((rental) => (
                <Grid item xs={12} sm={6} md={4} key={rental.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                        borderColor: 'primary.main'
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                      {/* Top status & room */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Chip
                          icon={<RoomIcon fontSize="small" />}
                          label={getRoomLabel(rental.arm_idf)}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 700 }}
                        />
                        <Chip
                          label={rental.status}
                          size="small"
                          color={getStatusColor(rental.status) as any}
                          icon={getStatusIcon(rental.status)}
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>

                      {/* Client Title */}
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', mb: 0.5 }}>
                        {rental.client_name}
                      </Typography>

                      {rental.lib && rental.lib !== `Location - ${rental.client_name}` && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                          {rental.lib}
                        </Typography>
                      )}

                      <Divider sx={{ my: 1.5 }} />

                      {/* Dates */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <CalendarIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('academy.room_rentals.dates') || 'Période'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatDateDisplay(rental.start_date)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            → {formatDateDisplay(rental.end_date)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Contact */}
                      {(rental.client_phone || rental.client_email) && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5, mt: 1 }}>
                          {rental.client_phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon fontSize="small" color="action" />
                              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                {rental.client_phone}
                              </Typography>
                            </Box>
                          )}
                          {rental.client_email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <EmailIcon fontSize="small" color="action" />
                              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                {rental.client_email}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Price Badge */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 1.2,
                          borderRadius: 2,
                          bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
                          mt: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MoneyIcon color="success" fontSize="small" />
                          <Typography variant="caption" color="success.dark" sx={{ fontWeight: 600 }}>
                            Tarif Total
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: 'success.dark' }}>
                          {Number(rental.price || 0).toLocaleString()} DZD
                        </Typography>
                      </Box>
                    </CardContent>

                    <Divider />

                    <CardActions sx={{ justifyContent: 'flex-end', px: 2, py: 1 }}>
                      <Tooltip title={t('common.edit') || 'Modifier'}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(rental)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete') || 'Supprimer'}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(rental)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        ) : (
          <GenericTable
            columns={tableColumns as any}
            data={rentalsList}
            onEdit={openEdit}
            onDelete={handleDelete}
            searchPlaceholder={t('academy.room_rentals.search_placeholder') || 'Rechercher une location, client, salle...'}
            filterColumns={['client_name', 'code', 'lib', 'client_phone', 'client_email']}
            maxHeight="calc(100vh - 280px)"
          />
        )}
      </GenericPage>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: FORM VIEW
  // ═══════════════════════════════════════════════════════════════════════════

  const isEditing = Boolean(formData.id && formData.id > 0);

  // Convert form data for GenericPage CRUD operations
  const formPayload = {
    ...formData,
    RNT_IDF: formData.id,
    ARM_IDF: formData.arm_idf,
    RNT_CODE: formData.code,
    RNT_LIB: formData.lib,
    RNT_CLIENT_NAME: formData.client_name,
    RNT_CLIENT_PHONE: formData.client_phone,
    RNT_CLIENT_EMAIL: formData.client_email,
    RNT_START_DATE: formData.start_date,
    RNT_END_DATE: formData.end_date,
    RNT_PRICE: formData.price,
    RNT_DEPOSIT: formData.deposit,
    RNT_STATUS: formData.status,
    RNT_NOTES: formData.notes
  };

  const footerInfo = {
    lastUpdate: formData.last_updt,
    userName: formData.user_lib
  };

  return (
    <GenericPage
      title={
        isEditing
          ? t('academy.room_rentals.edit_title', { name: formData.client_name || formData.code }) || `Modifier la location : ${formData.client_name || formData.code}`
          : t('academy.room_rentals.create_title') || 'Nouvelle Location de Salle'
      }
      icon={<EventAvailableIcon />}
      footerEnabled={true}
      footerInfo={footerInfo}
      additionalActions={
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={backToList}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {t('common.back_to_list') || 'Retour à la liste'}
        </Button>
      }
      crudConfig={{
        entityKey: 'Blobtic_Academy,academy_room_rental',
        primaryKey: 'RNT_IDF'
      }}
      data={formPayload}
      onDataChange={(savedData: any) => {
        if (savedData) {
          const updated = {
            ...formData,
            id: savedData.RNT_IDF || formData.id,
            rnt_uidf: savedData.RNT_UIDF || formData.rnt_uidf,
            code: savedData.RNT_CODE || formData.code,
            lib: savedData.RNT_LIB || formData.lib,
            last_updt: savedData.LAST_UPDT,
            user_lib: savedData.USER_LIB
          };
          setFormData(updated);
        }
      }}
      onValidate={handleValidate}
    >
      <Box sx={{ p: { xs: 1, sm: 2 } }}>
        <Grid container spacing={3}>
          {/* Card 1: Room & Schedule */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.04)', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <RoomIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t('academy.room_rentals.section_room') || 'Salle & Créneau'}
                </Typography>
              </Box>

              <Stack spacing={2.5}>
                <TextField
                  select
                  label={t('academy.room_rentals.room') || 'Salle à Louer'}
                  value={formData.arm_idf || ''}
                  onChange={(e) => setField('arm_idf', Number(e.target.value))}
                  fullWidth
                  required
                  helperText={t('academy.room_rentals.room_helper') || 'Sélectionnez la salle disponible pour cette location'}
                >
                  <MenuItem value="">
                    <em>{t('common.select') || 'Sélectionner une salle...'}</em>
                  </MenuItem>
                  {rooms.map((room) => (
                    <MenuItem key={room.id} value={room.id}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {room.room_number} — {room.floor}
                        </Typography>
                        <Chip size="small" label={`${room.capacity} places`} variant="outlined" />
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('academy.room_rentals.start_date') || 'Date & Heure Début'}
                      type="datetime-local"
                      value={formData.start_date || ''}
                      onChange={(e) => setField('start_date', e.target.value)}
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('academy.room_rentals.end_date') || 'Date & Heure Fin'}
                      type="datetime-local"
                      value={formData.end_date || ''}
                      onChange={(e) => setField('end_date', e.target.value)}
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('academy.room_rentals.code') || 'Code Référence'}
                      value={formData.code || ''}
                      onChange={(e) => setField('code', e.target.value)}
                      placeholder="Ex: LOC-2026-001"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label={t('common.status') || 'Statut'}
                      value={formData.status || 'CONFIRMED'}
                      onChange={(e) => setField('status', e.target.value as RoomRentalStatus)}
                      fullWidth
                    >
                      <MenuItem value="CONFIRMED">Confirmée</MenuItem>
                      <MenuItem value="PENDING">En attente / Devis</MenuItem>
                      <MenuItem value="COMPLETED">Terminée</MenuItem>
                      <MenuItem value="CANCELLED">Annulée</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Stack>
            </Card>
          </Grid>

          {/* Card 2: Client & Pricing */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.04)', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t('academy.room_rentals.section_client') || 'Client & Tarification'}
                </Typography>
              </Box>

              <Stack spacing={2.5}>
                <TextField
                  label={t('academy.room_rentals.client_name') || 'Nom du Client / Organisme'}
                  value={formData.client_name || ''}
                  onChange={(e) => setField('client_name', e.target.value)}
                  placeholder="Ex: Société ABC / Association XYZ"
                  fullWidth
                  required
                />

                <TextField
                  label={t('academy.room_rentals.lib') || 'Intitulé / Motif de la location'}
                  value={formData.lib || ''}
                  onChange={(e) => setField('lib', e.target.value)}
                  placeholder="Ex: Séminaire annuel de formation"
                  fullWidth
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('common.phone') || 'Téléphone Contact'}
                      value={formData.client_phone || ''}
                      onChange={(e) => setField('client_phone', e.target.value)}
                      placeholder="0550..."
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('common.email') || 'Email Contact'}
                      type="email"
                      value={formData.client_email || ''}
                      onChange={(e) => setField('client_email', e.target.value)}
                      placeholder="contact@client.dz"
                      fullWidth
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('academy.room_rentals.price') || 'Tarif Total (DZD)'}
                      type="number"
                      value={formData.price ?? 0}
                      onChange={(e) => setField('price', Number(e.target.value))}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('academy.room_rentals.deposit') || 'Acompte Versé (DZD)'}
                      type="number"
                      value={formData.deposit ?? 0}
                      onChange={(e) => setField('deposit', Number(e.target.value))}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Card>
          </Grid>

          {/* Card 3: Notes & Special Requirements */}
          <Grid item xs={12}>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <NotesIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t('academy.room_rentals.notes_title') || 'Notes & Équipements Requis'}
                </Typography>
              </Box>

              <TextField
                label={t('academy.room_rentals.notes') || 'Besoins particuliers, matériels demandés, dispositions spécifiques...'}
                value={formData.notes || ''}
                onChange={(e) => setField('notes', e.target.value)}
                multiline
                rows={3}
                fullWidth
                placeholder="Ex: Projecteur HD nécessaire, 2 micros sans fil, disposition en U..."
              />
            </Card>
          </Grid>
        </Grid>
      </Box>
    </GenericPage>
  );
};

export default RoomRentalsPage;
